import type { AppServices } from '../app';
import { FEEDBACK_MAX, type FeedbackKind } from '../../server/feedback';
import { STORE, fovDeg, loadJSON, place, placeSource, pointingPaused, previewFrom, reliving, savedText, settings, simNow, skyBand, timeLive, timeRate, viewMode, type Settings } from '../state';
import { isStandalone } from './install';

// Feedback (the owner, 24 October 2026, DECISIONS 112): a message, what it is about, and the details that help look
// into it, sent to Ahillah's server (server/feedback.ts). Nothing that names the person goes with it: no name, no
// email, and the place only to about 10 km. A message that cannot go now waits on the phone and goes when it can.

export type { FeedbackKind };

declare const __BUILD__: string;
/** Which build this is: the commit and when it was built (vite.config.ts); "dev" where it is not stamped. */
export const BUILD = typeof __BUILD__ === 'string' ? __BUILD__ : 'dev';

/** What a message says, as sent. */
export interface FeedbackMessage {
  v: 1;
  /** Its own id (server/feedback.ts), given when it is sent. */
  id: string;
  /** What it is about: a key for grouping, and the words the person chose. */
  about: { key: string; label: string };
  kind: FeedbackKind | null;
  text: string;
  context: Record<string, unknown>;
}

// ---------- Errors Ahillah met ----------
/** The last few, in memory only: never saved, and sent only with a message. */
const errors: { m: string; at: string; t: number }[] = [];
/** Addresses lose what follows ? or #: a weather request's position, or the key a move carries, never goes. */
export const scrub = (s: string) => s.replace(/[?#][^\s'")]*/g, '…');
const fileOf = (f: string) => scrub(f.split('/').pop() ?? '');

export function keepError(message: string, at = '') {
  errors.push({ m: scrub(message).slice(0, 200), at: at.slice(0, 80), t: Date.now() });
  if (errors.length > 5) errors.shift();
}

/** Listen for errors from the start (src/main.tsx). */
export function watchErrors() {
  addEventListener('error', (e) => keepError(e.message || 'error', e.filename ? `${fileOf(e.filename)}:${e.lineno}` : ''));
  addEventListener('unhandledrejection', (e) => {
    const r = (e as PromiseRejectionEvent).reason;
    keepError(r instanceof Error ? `${r.name}: ${r.message}` : String(r));
  });
}

// ---------- The details ----------
const round = (x: number, places = 1) => Math.round(x * 10 ** places) / 10 ** places;
/** Settings that change what the sky shows or how Ahillah behaves; none of the alerts'. */
const SETTINGS_SENT: (keyof Settings)[] = [
  'nakedEye', 'nightMode', 'arabic', 'arabicNames', 'skyCulture', 'starBoost', 'bortle', 'bortleAuto', 'milkyWay', 'landscape',
  'seeThroughGround', 'constellationLines', 'starNames', 'deepSky', 'satellites', 'meteors', 'survey', 'twinkle', 'startPointing',
  'eyepiece', 'cameraFeed', 'arabicVoice',
];

/**
 * What goes with a message: which build, the phone, the sky as it was shown, the place to about 10 km, the compass, the
 * last few errors and the last few things looked at (`seen`, as the person saw them named).
 */
export function feedbackContext(s: AppServices | null, seen: string[]): Record<string, unknown> {
  const st = settings.peek();
  const pl = place.peek();
  const live = timeLive.peek();
  const view = s?.engine.viewCentre();
  const o = s?.engine.orientation;
  return {
    build: BUILD,
    // When it was written: one that waited on the phone arrives later.
    written: new Date().toISOString(),
    device: {
      ua: navigator.userAgent.slice(0, 300),
      installed: isStandalone(),
      screen: `${innerWidth}x${innerHeight}@${round(devicePixelRatio, 2)}`,
      lang: navigator.language,
    },
    sky: {
      mode: viewMode.peek(),
      paused: pointingPaused.peek(),
      live,
      ...(live ? {} : { shown: simNow().toISOString(), rate: timeRate.peek() }),
      preview: previewFrom.peek()?.kind ?? null,
      reliving: !!reliving.peek(),
      band: skyBand.peek(),
      ...(view ? { alt: round(view.alt), az: round(view.az), fov: round(fovDeg.peek()) } : {}),
    },
    place: { lat: round(pl.lat), lon: round(pl.lon), tz: pl.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone, from: placeSource.peek() },
    ...(o ? { compass: { status: o.status, north: o.northKnown, accuracy: o.headingAccuracy, aligned: round(o.calibration), declination: round(o.declination) } } : {}),
    settings: Object.fromEntries(SETTINGS_SENT.map((k) => [k, st[k]])),
    errors: errors.map((e) => ({ m: e.m, at: e.at, ago: Math.round((Date.now() - e.t) / 1000) })),
    seen: seen.slice(0, 6),
  };
}

// ---------- Sending, now or later ----------
// Each message is put in the outbox on the phone first and taken out once the server has it (or will never take it),
// so closing Ahillah while it sends loses nothing. Each has its own id, so one sent twice is kept once.
/** Messages waiting to go, oldest first: at most 10, for at most 30 days. */
const OUTBOX = 'feedbackOutbox';
const OUTBOX_MAX = 10;
const OUTBOX_DAYS = 30;
interface Waiting { at: number; body: string }
const waiting = (): Waiting[] =>
  loadJSON<Waiting[]>(OUTBOX, []).filter((x) => !!x && typeof x.at === 'number' && typeof x.body === 'string' && x.body.length <= FEEDBACK_MAX && Date.now() - x.at < OUTBOX_DAYS * 86400e3);
/** Write the outbox; false when the phone would not keep it (private browsing, storage full). */
function keepWaiting(list: Waiting[]): boolean {
  try {
    if (list.length) localStorage.setItem(STORE + OUTBOX, JSON.stringify(list));
    else localStorage.removeItem(STORE + OUTBOX);
    return true;
  } catch {
    return false;
  }
}
const takeOut = (x: Waiting) => keepWaiting(waiting().filter((y) => !(y.at === x.at && y.body === x.body)));
/** The outbox without what is a month old, written back so nothing stays on the phone longer. */
function prune(): Waiting[] {
  const list = waiting();
  const saved = savedText(OUTBOX);
  if (saved !== null && saved !== JSON.stringify(list)) keepWaiting(list);
  return list;
}

/** A new message's own id: 128 random bits, URL-safe. */
const newId = () => btoa(String.fromCharCode(...crypto.getRandomValues(new Uint8Array(16)))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

/** 'ok' once kept; 'retry' when the server cannot be reached or asks to wait; 'drop' when it will never take it. */
async function post(body: string): Promise<'ok' | 'retry' | 'drop'> {
  const stop = new AbortController();
  const timer = setTimeout(() => stop.abort(), 15000);
  try {
    const r = await fetch('/api/feedback', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, signal: stop.signal });
    if (r.ok) return 'ok';
    // Too many today, the server busy, or an older server without feedback yet: later. Anything else never will.
    return r.status === 429 || r.status === 404 || r.status >= 500 ? 'retry' : 'drop';
  } catch {
    return 'retry';
  } finally {
    clearTimeout(timer);
  }
}

/** 'sent'; 'offline' or 'later' when it waits on the phone to go when it can; 'failed' when it could not be sent or
 *  kept (the server refused it, ten are already waiting, or the phone would not keep it), so the words stay to try again. */
export type SendOutcome = 'sent' | 'offline' | 'later' | 'failed';

export async function sendFeedback(m: Omit<FeedbackMessage, 'id'>): Promise<SendOutcome> {
  const body = JSON.stringify({ ...m, id: newId() });
  const list = prune();
  // A full outbox never pushes out a message already promised.
  if (list.length >= OUTBOX_MAX) return 'failed';
  const mine = { at: Date.now(), body };
  const kept = keepWaiting([...list, mine]);
  const r = await post(body);
  if (r === 'ok') {
    if (kept) takeOut(mine);
    void flushOutbox();
    return 'sent';
  }
  if (r === 'drop') {
    if (kept) takeOut(mine);
    return 'failed';
  }
  // Waiting to go: only if the phone kept it.
  if (!kept) return 'failed';
  return navigator.onLine === false ? 'offline' : 'later';
}

let flushing = false;
/** Send what is waiting, oldest first, until the server cannot be reached (or the phone will not let one be taken out,
 *  which would send it again and again). */
export async function flushOutbox() {
  if (flushing) return;
  flushing = true;
  try {
    for (;;) {
      const next = prune()[0];
      if (!next) break;
      const r = await post(next.body);
      if (r === 'retry' || !takeOut(next)) break;
    }
  } finally {
    flushing = false;
  }
}

/** Waiting messages go a few seconds after Ahillah opens, whenever the phone comes back online or Ahillah back to the
 *  screen, and every quarter of an hour while Ahillah is open (after the server asked to wait). Returns the clean-up. */
export function watchOutbox(): () => void {
  const go = () => void flushOutbox();
  const t = prune().length ? setTimeout(go, 8000) : undefined;
  const any = () => {
    if (waiting().length) go();
  };
  const every = setInterval(any, 15 * 60e3);
  const back = () => document.visibilityState === 'visible' && any();
  addEventListener('online', go);
  if (typeof document !== 'undefined') document.addEventListener('visibilitychange', back);
  return () => {
    clearTimeout(t);
    clearInterval(every);
    removeEventListener('online', go);
    if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', back);
  };
}
